import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';

import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import ConfirmDialog from '../../shared/confirmDialog.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import { asyncValidateScenario, validateScenario } from './validators';
import getColumnDefinitions from './columnDefinitions';
import { ScenarioPropType, ScenarioListColumnState } from '../shared/propTypes';
import { GridMode, useEditableGridLogic } from '../../shared/gridEdit/useEditableGridLogic';
import { useColumnStateInStore } from '../../shared/hooks/useColumnStateInStore.hook';

import { useUpdateScenario, useAddScenario } from '../../shared/mutations/scenarios';

import agGridConfig from '../../agGridConfig';
import { OptionsPropTypes } from '../../administration/customGroupsPage/propTypes';

const compareScenario = (first, second) =>
  first.publicFlag === second.publicFlag
  && ((first.scenarioId === null && second.scenarioId === null) || first.scenarioId === second.scenarioId);

const createScenario = (_, scenarioToCopy) => ({
  scenarioName: '',
  periodName: scenarioToCopy ? scenarioToCopy.periodName : '',
  dataSource: scenarioToCopy ? scenarioToCopy.dataSource : '',
  sourceName: scenarioToCopy ? scenarioToCopy.scenarioName : '',
  publicFlag: 'N',
  lockedFlag: 'N',
});


const ScenariosGrid = ({
  selectRow,
  scenarios,
  periods,
  isLoading,
  isDeleting,
  canCreatePublicScenario,
  scenarioListColumnState,
  updateColumnState,
}) => {
  const [mode, setMode] = useState(GridMode.View);
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);
  const [isResetting, setResetting] = useState(false);

  const showCancelDialog = useCallback(() => setIsCancelDialogVisible(true), [setIsCancelDialogVisible]);
  const hideCancelDialog = useCallback(() => setIsCancelDialogVisible(false), [setIsCancelDialogVisible]);
  const shouldShowNavigationPrompt = mode === GridMode.Add || mode === GridMode.Edit;

  const onSaveCallback = useCallback((__, scenario) => {
    setMode(GridMode.View);
    selectRow({
      name: agGridConfig.scenariosList.name,
      fields: agGridConfig.scenariosList.fields,
      data: scenario,
    });
  }, [setMode, selectRow]);

  const { mutate: updateScenario, isLoading: isUpdating } = useUpdateScenario(onSaveCallback);
  const { mutate: addScenario, isLoading: isAdding } = useAddScenario(onSaveCallback);

  const onSave = useCallback(async ({ addedRows, updatedRows, hasErrors }) => {
    if (hasErrors) {
      return;
    }

    if (mode === GridMode.Add) {
      await addScenario(addedRows[0]);
    }

    if (mode === GridMode.Edit) {
      if (updatedRows.length) {
        await updateScenario(updatedRows[0]);
      } else {
        setMode(GridMode.View);
      }
    }
  }, [mode, addScenario, updateScenario]);

  const onReset = () => {
    setResetting(false);
  };

  const onCancel = (hasChanges, reset) => {
    if (hasChanges) {
      showCancelDialog();
    } else {
      setMode(GridMode.View);
      reset();
    }
  };

  const context = useMemo(() => ({
    scenarios,
    periods,
    canCreatePublicScenario,
  }), [
    scenarios,
    periods,
    canCreatePublicScenario,
  ]);

  const {
    onGridReady,
    reset,
    save,
    cancel,
    setRowData,
    stopEditing,
    addRow,
    getRowNodeId,
    editedRowId,
    setEditedRow,
    postSort,
    onSortChanged: onSortChangedEditableGridLogicHook,
    onFilterChanged: onFilterChangedEditableGridLogicHook,
    onColumnVisible,
    arePendingValidations,
    gridApi,
    onSelectionChanged,
  } = useEditableGridLogic({
    data: scenarios,
    idField: 'scenarioId',
    compareRow: compareScenario,
    createRow: createScenario,
    validateRow: validateScenario,
    asyncValidateRow: asyncValidateScenario,
    mode,
    onSave,
    onReset,
    onCancel,
    context,
  });

  const {
    onFilterChanged: onFilterChangedColumnStateHook,
    onSortChanged: onSortChangedColumnStateHook,
    reloadColumnStateFromStore,
  } = useColumnStateInStore({
    columnState: scenarioListColumnState,
    updateColumnState,
    gridApi,
  });

  // reload column state after every data change
  useEffect(() => {
    reloadColumnStateFromStore();
  }, [scenarios, reloadColumnStateFromStore]);

  const composedOnSortChanged = useCallback(params => {
    onSortChangedColumnStateHook(params);
    onSortChangedEditableGridLogicHook(params);
  }, [onSortChangedColumnStateHook, onSortChangedEditableGridLogicHook]);

  const composedOnFilterChanged = useCallback(params => {
    onFilterChangedColumnStateHook(params);
    onFilterChangedEditableGridLogicHook(params);
  }, [onFilterChangedColumnStateHook, onFilterChangedEditableGridLogicHook]);

  const editRow = useCallback(data => {
    setMode(GridMode.Edit);
    setEditedRow(data);
  }, [setEditedRow]);

  const cloneScenario = useCallback(scenarioToClone => {
    setMode(GridMode.Add);
    addRow(scenarioToClone);
  }, [setMode, addRow]);

  const columnDefinitions = useMemo(() => getColumnDefinitions({
    scenarios,
    setRowData,
    editRow,
    cloneScenario,
    stopEditing,
    periods,
    canCreatePublicScenario,
    context: { mode, editedRowId },
  }),
  [scenarios, editRow, setRowData, stopEditing, periods, canCreatePublicScenario, mode, editedRowId, cloneScenario]);

  const onCancelClick = useCallback(() => {
    cancel();
  }, [cancel]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    addRow();
  }, [setMode, addRow]);

  const onSaveClick = useCallback(() => {
    save();
  }, [save]);

  const confirmCancel = useCallback(() => {
    setResetting(true);
    reset();
    hideCancelDialog();
    setMode(GridMode.View);
  }, [reset, hideCancelDialog, setMode, setResetting]);

  const isGridLoading = isLoading || isResetting || isUpdating || isAdding || isDeleting || arePendingValidations;


  return (
    <div className="ag-grid-full-page-with-secondary-menu">
      <div className="row no-gutters page-content-container">
        <div className="col-1 ml-auto d-flex justify-content-center">
          {
            mode === GridMode.View ? (
              <Button
                type="button"
                kind="secondary"
                disabled={isGridLoading}
                onClick={onAddClick}
              >
                Add Scenario
              </Button>
            ) : null
          }
          {
            mode === GridMode.Add || mode === GridMode.Edit ? (
              <Button
                type="button"
                kind="secondary"
                disabled={isGridLoading}
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            ) : null
          }
        </div>
        <div className="col-1 d-flex justify-content-center">
          {
            mode === GridMode.Add || mode === GridMode.Edit ? (
              <Button
                type="button"
                kind="primary"
                disabled={isGridLoading}
                onClick={onSaveClick}
              >
                Save
              </Button>
            ) : null
          }
        </div>
      </div>
      <AgGrid
        className="ag-theme-bootstrap"
        columnDefs={columnDefinitions}
        isGridLoading={isGridLoading || isAdding || isDeleting}
        onGridReady={onGridReady}
        getRowId={getRowNodeId}
        enableBrowserTooltips
        stopEditingWhenCellsLoseFocus
        suppressCellFocus
        suppressMultiRangeSelection
        enableRangeSelection
        enableFillHandle
        suppressCopyRowsToClipboard
        suppressScrollOnNewData
        context={{
          mode,
          editedRowId,
        }}
        postSort={postSort}
        onSortChanged={composedOnSortChanged}
        onFilterChanged={composedOnFilterChanged}
        onColumnVisible={onColumnVisible}
        onSelectionChanged={onSelectionChanged}
        withSearchBar
      />
      <div className="d-flex flex-row-reverse mt-2 page-content-container" />
      <ConfirmDialog
        onCancel={hideCancelDialog}
        onConfirm={confirmCancel}
        message="Are you sure you want to discard the changes?"
        title="Confirm Cancel"
        dismissText="No"
        submitText="Yes"
        isVisible={isCancelDialogVisible}
      />
      <NavigationPrompt show={shouldShowNavigationPrompt} />
      <ReactTooltip place="right" effect="float" />
    </div>
  );
};

ScenariosGrid.propTypes = {
  scenarios: PropTypes.arrayOf(ScenarioPropType),
  periods: OptionsPropTypes,
  selectRow: PropTypes.func,
  addScenario: PropTypes.func,
  updateScenario: PropTypes.func,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  isDeleting: PropTypes.bool,
  canCreatePublicScenario: PropTypes.bool,
  scenarioListColumnState: ScenarioListColumnState,
  updateColumnState: PropTypes.func,
};

export default ScenariosGrid;
